import type { ProjektVerortung } from '../../types';
import type { VerortungGeraet } from './geraetEinstellung';
import { verortungEingerichtet } from './projektVerortung';
import { geschossEbenen, geruestLagen } from './ebenen';
import { planGeoreferenz } from './projektVerortung';

/**
 * Warum die automatische Verortung im Mangel-Wizard (nicht) greift —
 * in Klartext für den Schritt „Wo“ (Betreiber 27.09.: „es findet keine
 * automatische Verortung statt, man muss den Ort eintippen“).
 *
 * Reihenfolge = Reihenfolge der Abhilfe: erst App, dann Schalter, dann
 * Einrichtung. Im Browser gibt es keine Meldung (dort gibt es die Funktion nicht).
 */

export interface VerortungsDiagnose {
    /** Läuft die automatische Verortung? */
    nutzbar: boolean;
    /** Warum nicht (Klartext mit Abhilfe); null im Browser oder wenn nutzbar. */
    grund: string | null;
    /** Einschränkungen, obwohl nutzbar (z. B. „kein Plan georeferenziert → kein Pin“). */
    hinweise: string[];
}

export function verortungDiagnose(e: {
    app: boolean;
    plugin: boolean;
    geraet: VerortungGeraet;
    projekt: ProjektVerortung;
}): VerortungsDiagnose {
    const { app, plugin, geraet, projekt } = e;
    const aus = (grund: string | null): VerortungsDiagnose => ({ nutzbar: false, grund, hinweise: [] });
    if (!app) return aus(null);
    if (!plugin) return aus('App-Version zu alt — in App-Info auf 1.0.2 oder neuer aktualisieren.');
    if (!geraet.aktiv) return aus('Auf diesem Gerät ausgeschaltet (App-Info → Kamera mit Ort-Einblendung).');
    if (!projekt.aktiv) return aus('Im Projekt ausgeschaltet (Pläne → Sensor-Verortung → „Im Projekt aktiv“).');
    if (!verortungEingerichtet(projekt)) {
        return aus(
            'Im Projekt noch nicht eingerichtet: unter Pläne → Sensor-Verortung die Geschosshöhen eintragen '
            + '(und Pläne einem Geschoss zuordnen). Punkte im Messmodus (App-Info) gelten nur für den Test auf diesem Gerät.',
        );
    }
    const hinweise: string[] = [];
    const ebenen = geschossEbenen(projekt.geschosse ?? []).length + geruestLagen(projekt.geruest).length;
    if (ebenen === 0) hinweise.push('Keine Geschosshöhen eingetragen — nur Pin per GPS, kein Geschoss.');
    const plaene = Object.values(projekt.plaene ?? {});
    const georeferenziert = plaene.some((p) => {
        const g = planGeoreferenz(p);
        return !!g && !('fehler' in g);
    });
    if (!georeferenziert) hinweise.push('Kein Plan georeferenziert — Geschoss ja, aber kein Pin auf dem Plan.');
    if (ebenen > 0 && !plaene.some((p) => p.art !== 'lageplan' && !!p.geschossId)) {
        hinweise.push('Kein Plan einem Geschoss zugeordnet — der Pin kann keinem Geschossplan folgen.');
    }
    return { nutzbar: true, grund: null, hinweise };
}
